// frontend/src/pages/BioGeneratorPage.js
//
// Renders /tools/instagram-bio-generator, /tools/tiktok-bio-generator and
// /tools/linkedin-bio-generator from data/bio-generator-pages.json.
//
// SIBLING PAGES, NOT PILLAR-AND-SPOKE. Each targets its own head term —
// `instagram bio generator` (3 phrasings, 15,000, comp 1), `tiktok bio` (5,000,
// comp 0), `linkedin bio` (4 phrasings, 20,000, comp 14) — so each gets an
// exact-match URL rather than hanging off a parent that would rank for none of
// them.
//
// HOW WE BEAT AN ESTABLISHED-SaaS SERP. Hootsuite, Mention, ContentStudio,
// Postiz, RADAAR, Birdeye, Blotato and Storrito all run the same thin lead
// magnet. We will not take the head term on domain authority. We take the long
// tail their pages ignore — `create bio for instagram`, `how to make a bio for
// instagram`, `bio for ig`, `bio for tiktok`, `linked in bio examples`, all at
// competition 0-3 — which is what the how-to and worked-examples sections below
// exist to serve. Those sections are also why this page is worth landing on
// even for someone who never touches the generator.
//
// useLocation, NOT useParams: static routes, no :param. useParams returns
// undefined here and silently renders the 404 under a correct prerendered title.
import '../custom.css'

import React from 'react'
import { Container, Row, Col, Button, Accordion, Image } from 'react-bootstrap'
import { useLocation } from 'react-router-dom'
import { CheckCircleFill } from 'react-bootstrap-icons'

import Seo, { SITE_URL } from '../components/Seo'
import BioGenerator from '../components/BioGenerator'
import pages from '../data/bio-generator-pages.json'
import NotFound from './NotFound'

const HERO = {
  'instagram-bio-generator': ['/assets/images/articles/bio-instagram.webp', 'Someone editing their profile on a phone'],
  'tiktok-bio-generator':    ['/assets/images/articles/bio-tiktok.webp',    'A creator holding a phone showing a profile'],
  'linkedin-bio-generator':  ['/assets/images/articles/bio-linkedin.webp',  'A person working at a laptop in an office'],
}

export default function BioGeneratorPage () {
  const { pathname } = useLocation()
  const slug = pathname.replace(/^\/+tools\/+/, '').replace(/\/+$/, '')
  const p = pages.find(x => x.slug === slug)
  if (!p) return <NotFound />

  const url = `/tools/${p.slug}`
  const [heroSrc, heroAlt] = HERO[p.slug] || HERO['instagram-bio-generator']

  return (
    <>
      <Seo
        title={p.seoTitle}
        description={p.seoDescription}
        path={url}
        image={heroSrc}
        jsonLd={[
          {
            '@context': 'https://schema.org', '@type': 'FAQPage',
            mainEntity: p.faqs.map(([q, a]) => ({
              '@type': 'Question', name: q,
              acceptedAnswer: { '@type': 'Answer', text: a },
            })),
          },
          {
            '@context': 'https://schema.org', '@type': 'WebApplication',
            name: p.h1,
            url: `${SITE_URL}${url}`,
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Any',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          },
          {
            '@context': 'https://schema.org', '@type': 'HowTo',
            name: p.howto_heading,
            step: p.howto.map(([name, text], i) => ({
              '@type': 'HowToStep', position: i + 1, name, text,
            })),
          },
        ]}
      />

      {/* ───────── HERO ───────── */}
      <section className='full-bleed py-5 bg-primary-soft'>
        <Container fluid='lg'>
          <Row className='align-items-center g-4'>
            <Col lg={7}>
              <h1 className='display-5 fw-bold mb-3'>{p.h1}</h1>
              <p className='lead mb-0'>{p.sub}</p>
            </Col>
            <Col lg={5}>
              <Image src={heroSrc} alt={heroAlt} fluid rounded loading='eager'
                     width={1200} height={800} className='shadow-sm' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── THE TOOL — it is the search intent ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 900 }}>
          <BioGenerator platform={p.platform} />
        </Container>
      </section>

      {/* ───────── THE ANSWER (snippet target) ───────── */}
      <section className='py-5 bg-light'>
        <Container style={{ maxWidth: 760 }}>
          {/* eslint-disable-next-line react/no-danger */}
          <p className='fs-5' dangerouslySetInnerHTML={{ __html: p.intro }} />
          <p className='text-muted fs-5 mb-0'>{p.intro2}</p>
        </Container>
      </section>

      {/* ───────── HOW TO (serves the how-to long tail at comp 0-3) ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 780 }}>
          <h2 className='fw-bold mb-4'>{p.howto_heading}</h2>
          {p.howto.map(([heading, body], i) => (
            <div className='d-flex gap-3 mb-4' key={heading}>
              <div className='fw-bold text-primary flex-shrink-0'
                   style={{ fontSize: '1.5rem', lineHeight: 1.2, width: 32 }}>
                {i + 1}
              </div>
              <div>
                <div className='fw-semibold mb-1'>{heading}</div>
                <p className='text-muted mb-0'>{body}</p>
              </div>
            </div>
          ))}
        </Container>
      </section>

      {/* ───────── WORKED EXAMPLES — every one verified against the limit ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 900 }}>
          <h2 className='fw-bold mb-2'>{p.examples_heading}</h2>
          <p className='text-muted mb-4'>{p.examples_intro}</p>
          <Row className='g-3'>
            {p.examples.map(([label, text]) => (
              <Col md={6} key={label}>
                <div className='p-3 rounded-3 h-100' style={{ background: '#fff', border: '1px solid #e5e7eb' }}>
                  <div className='text-muted small text-uppercase mb-2' style={{ letterSpacing: '.04em' }}>
                    {label}
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{text}</div>
                  <div className='small text-muted mt-2' style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {[...text].length} / {p.max} characters
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ───────── DEPTH ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          {p.sections.map(([heading, body]) => (
            <div className='mb-4' key={heading}>
              <h2 className='fw-bold h4 mb-2'>{heading}</h2>
              <p className='text-muted mb-0'>{body}</p>
            </div>
          ))}
        </Container>
      </section>

      {/* ───────── WHAT IS GREATLY SOCIAL (standing rule 4) ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 940 }}>
          <Row className='align-items-center g-4'>
            <Col md={6}>
              <h2 className='fw-bold mb-3'>What is Greatly Social?</h2>
              <p className='text-muted'>
                Greatly Social is a social media scheduler and content calendar. At Greatly
                Social you can write a post once, give it a different caption per network, and
                schedule it to all eight — Instagram, TikTok, Facebook, LinkedIn, YouTube,
                Pinterest, Threads and X — from one calendar.
              </p>
              <p className='text-muted'>
                It also builds the page your bio link points at, and keeps it current with your
                latest posts. Plans start at $9/month and the trial needs no card.
              </p>
              <Button variant='primary' href='/register' className='me-2'>
                Start using Greatly Social free
              </Button>
              <Button variant='outline-primary' href='/plans'>See plans</Button>
            </Col>
            <Col md={6}>
              {/* A real capture of the product, not stock — see standing rule 2. */}
              <Image src='/assets/logo/calender.webp' alt='The Greatly Social content calendar'
                     fluid rounded className='shadow-sm' loading='lazy' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── CROSS-LINKS ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold h4 mb-3'>More free tools</h2>
          <Row className='g-3'>
            {pages.filter(x => x.slug !== p.slug).map(x => (
              <Col md={4} key={x.slug}>
                <a href={`/tools/${x.slug}`} className='d-block p-3 rounded-3 text-decoration-none h-100'
                   style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                  <div className='fw-semibold'>{x.platformLabel} {x.field} generator</div>
                  <div className='text-muted small'>{x.max} characters, checked</div>
                </a>
              </Col>
            ))}
            <Col md={4}>
              <a href='/tools/engagement-rate-calculator' className='d-block p-3 rounded-3 text-decoration-none h-100'
                 style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                <div className='fw-semibold'>Engagement rate calculator</div>
                <div className='text-muted small'>Per-platform formulas</div>
              </a>
            </Col>
            <Col md={4}>
              <a href='/best-time-to-post' className='d-block p-3 rounded-3 text-decoration-none h-100'
                 style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                <div className='fw-semibold'>Best time to post</div>
                <div className='text-muted small'>A schedule for your audience</div>
              </a>
            </Col>
            <Col md={4}>
              <a href='/link-in-bio' className='d-block p-3 rounded-3 text-decoration-none h-100'
                 style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                <div className='fw-semibold'>Link in bio</div>
                <div className='text-muted small'>The page under your bio</div>
              </a>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── PRODUCT ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold mb-3'>The bio is the easy part</h2>
          <p className='text-muted fs-5'>
            Keeping the account fed is the hard part. At Greatly Social you can schedule a week
            across eight networks in one sitting.
          </p>
          <Row className='g-3 mb-4'>
            {[['Schedule once, publish weekly', 'Set your slots and let them run.'],
              ['Eight networks, one post', 'A separate caption per network on the same post.'],
              ['A link page that updates itself', 'It pulls in your latest posts, so it never goes stale.'],
              ['See the gaps', 'Empty days are obvious on a calendar, invisible in a list.']].map(([t, b]) => (
              <Col md={6} key={t}>
                <div className='d-flex align-items-start gap-2'>
                  <CheckCircleFill className='text-success mt-1 flex-shrink-0' />
                  <div><div className='fw-semibold'>{t}</div><div className='text-muted small'>{b}</div></div>
                </div>
              </Col>
            ))}
          </Row>
          <Button variant='primary' size='lg' href='/register' className='me-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-primary' size='lg' href='/plans'>See plans from $9/month</Button>
        </Container>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-4 text-center'>Common questions</h2>
          <Accordion flush defaultActiveKey='0'>
            {p.faqs.map(([q, a], i) => (
              <Accordion.Item eventKey={String(i)} key={i}>
                <Accordion.Header>{q}</Accordion.Header>
                <Accordion.Body className='text-muted'>{a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      {/* ───────── CTA ───────── */}
      <section className='full-bleed py-5 text-center bg-primary text-white'>
        <div className='mx-auto px-4' style={{ maxWidth: 720 }}>
          <h2 className='fw-bold mb-3'>At Greatly Social you can keep the account going, not just the bio</h2>
          <p className='lead mb-4'>
            Schedule a week across eight networks in one sitting. Free for 14 days, no card,
            plans from $9/month.
          </p>
          <Button variant='light' size='lg' href='/register' className='me-2 mb-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-light' size='lg' href='/link-in-bio' className='mb-2'>
            See link in bio
          </Button>
        </div>
      </section>
    </>
  )
}
